import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plug } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Tabs } from '@/components/ui/Tabs';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Input } from '@/components/ui/Input';
import { usePageContext } from '@/hooks/usePageContext';
import {
  getCommandCenter,
  type CommandCenter,
} from '@/services/marketingIntegrations.service';
import OverviewTab from './ads/OverviewTab';
import PaidMediaTab from './ads/PaidMediaTab';
import SeoTab from './ads/SeoTab';
import SiteContentTab from './ads/SiteContentTab';
import FunnelTab from './ads/FunnelTab';
import GrowthHacksTab from './ads/GrowthHacksTab';
import RoadmapTab from './ads/RoadmapTab';
import KpiTab from './ads/KpiTab';

const TARGET_CPA_KEY = 'ftp-cmd-center-target-cpa-v1';

export default function AdsDashboardPage() {
  const updateAIContext = usePageContext('Growth Command Center');

  // Persisted target CPA (used as a threshold across tabs)
  const [targetCpa, setTargetCpa] = useState<number>(() => {
    const stored = typeof window !== 'undefined' ? localStorage.getItem(TARGET_CPA_KEY) : null;
    return stored ? Number(stored) || 200 : 200;
  });
  useEffect(() => {
    try { localStorage.setItem(TARGET_CPA_KEY, String(targetCpa)); } catch { /* ignore */ }
  }, [targetCpa]);

  const { data, isLoading } = useQuery<CommandCenter>({
    queryKey: ['marketing', 'command-center'],
    queryFn: getCommandCenter,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (!data) return;
    const summary = (Object.keys(data.dashboard) as Array<keyof CommandCenter['dashboard']>)
      .map((k) => `${k}=${data.dashboard[k].configured ? data.dashboard[k].status : 'not connected'}`)
      .join(', ');
    updateAIContext({ dataSummary: summary });
  }, [data, updateAIContext]);

  const tabs = !data
    ? [{ id: 'loading', label: 'Loading…', content: <div className="flex items-center justify-center py-20"><Spinner /></div> }]
    : [
        { id: 'overview',  label: 'Overview',           content: <OverviewTab cc={data} targetCpa={targetCpa} /> },
        { id: 'channels',  label: 'Ad Channel Findings', content: <PaidMediaTab cc={data} targetCpa={targetCpa} /> },
        { id: 'seo',       label: 'SEO Intelligence',   content: <SeoTab cc={data} /> },
        { id: 'site',      label: 'Site & Content',     content: <SiteContentTab cc={data} /> },
        { id: 'funnel',    label: 'Funnel & CRO',       content: <FunnelTab cc={data} /> },
        { id: 'hacks',     label: '10 Growth Hacks',    content: <GrowthHacksTab cc={data} /> },
        { id: 'roadmap',   label: '30-60-90 Roadmap',   content: <RoadmapTab cc={data} /> },
        { id: 'kpi',       label: 'KPI Dashboard',      content: <KpiTab cc={data} targetCpa={targetCpa} /> },
      ];

  return (
    <div className="w-full min-w-0">
      <div className="px-6 pt-6">
        <PageHeader
          title="Growth Command Center"
          subtitle="Live performance from Google Analytics, Google Ads, Meta and Search Console. Every finding references a specific number."
          actions={
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500 whitespace-nowrap">Target CPA</label>
              <Input
                type="number"
                value={targetCpa}
                onChange={(e) => setTargetCpa(Math.max(1, Number(e.target.value) || 0))}
                className="w-24 text-sm"
              />
              <Link to="/admin/marketing-integrations">
                <Button variant="ghost" size="sm">
                  <Plug className="h-4 w-4 mr-1" />
                  Manage
                </Button>
              </Link>
            </div>
          }
        />
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center py-20"><Spinner /></div>
      ) : (
        <Tabs tabs={tabs} defaultTab="overview" className="px-6" />
      )}
    </div>
  );
}
